import React, { useState, useMemo } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Save, Check, Trash2, Cpu } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useBookingConfig } from '@/lib/BookingConfigContext';

const inputCls = 'bg-[#111115] border-white/10 text-white h-11 placeholder:text-white/20 focus:border-primary/60';
const slugify = (s) => s.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

// Default allowed_rigs when an experience has never had any selected:
// experiences whose key or tag suggests a junior session get child_sim only;
// everything else gets all adult rigs.
function inferDefaultRigs(record, defaultData) {
  if (defaultData?.allowed_rigs?.length) return defaultData.allowed_rigs;
  const tag = (record?.tag || defaultData?.tag || '').toLowerCase();
  const key = (record?.key || '').toLowerCase();
  const isJunior = tag.includes('ages 5') || tag.includes('ages 8') || tag.includes('8–15') || tag.includes('5–7') || key.includes('child') || key.includes('young');
  return isJunior ? ['child_sim'] : ['sabelt_amg', 'sabelt_porsche', 'treq_fanatec'];
}

export default function BookingExperienceEditor({ experienceKey, defaultData, record, isNew, onSaved, onCreated, onDelete }) {
  const { rigs } = useBookingConfig();

  const [key, setKey] = useState(record?.key ?? '');
  const [name, setName] = useState(record?.name ?? defaultData?.name ?? '');
  const [tag, setTag] = useState(record?.tag ?? defaultData?.tag ?? '');
  const [price, setPrice] = useState(record?.price ?? defaultData?.price ?? 0);
  const [durationMinutes, setDurationMinutes] = useState(record?.duration_minutes ?? defaultData?.duration_minutes ?? 50);
  const [specs, setSpecs] = useState((record?.specs ?? defaultData?.specs ?? []).join('\n'));
  const [allowedRigs, setAllowedRigs] = useState(
    Array.isArray(record?.allowed_rigs) && record.allowed_rigs.length
      ? record.allowed_rigs
      : inferDefaultRigs(record, defaultData)
  );
  const [active, setActive] = useState(record?.active ?? true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const resolvedKey = record?.key ?? experienceKey ?? key;

  // Build the selectable rig list from the live BookingRig records (merged
  // with defaults). Falls back to the default rig keys if none exist yet.
  const rigList = useMemo(() => {
    const all = Object.entries(rigs || {})
      .map(([k, r]) => ({ key: k, ...r }))
      .filter((r) => r.active !== false)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    return all.length ? all : [
      { key: 'sabelt_amg', name: 'SABELT AMG', tag: 'ADULT RIG' },
      { key: 'sabelt_porsche', name: 'SABELT PORSCHE', tag: 'ADULT RIG' },
      { key: 'treq_fanatec', name: 'TREQ ACE 1', tag: 'ADULT RIG' },
      { key: 'child_sim', name: 'CHILD SIM RIG', tag: 'JUNIOR RIG' },
    ];
  }, [rigs]);

  const toggleRig = (rigKey) => {
    setAllowedRigs((prev) =>
      prev.includes(rigKey) ? prev.filter((k) => k !== rigKey) : [...prev, rigKey]
    );
  };

  const handleSave = async () => {
    const finalKey = isNew ? slugify(key) : resolvedKey;
    if (!finalKey) return;
    if (!allowedRigs.length) return;
    setSaving(true);
    setSaved(false);
    const payload = {
      key: finalKey,
      name,
      tag,
      price: Number(price) || 0,
      duration_minutes: Math.min(50, Math.max(1, Number(durationMinutes) || 50)),
      specs: specs.split('\n').map((s) => s.trim()).filter(Boolean),
      allowed_rigs: allowedRigs,
      active,
    };
    try {
      if (record?.id) {
        await base44.entities.BookingExperience.update(record.id, payload);
      } else {
        await base44.entities.BookingExperience.create(payload);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      if (isNew) { onCreated?.(); } else { onSaved?.(); }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!record?.id) return;
    setDeleting(true);
    try {
      await base44.entities.BookingExperience.delete(record.id);
      onDelete?.(record);
    } finally {
      setDeleting(false);
    }
  };

  const canDelete = !!record?.id && !defaultData;
  const canSave = !!name.trim() && !!key.trim() && allowedRigs.length > 0 && !saving;

  return (
    <div className="bg-[#111115] border border-white/5 p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="min-w-0">
          <span className="text-primary text-[10px] font-bold tracking-widest block mb-1">{isNew ? 'NEW EXPERIENCE' : (resolvedKey || 'EXPERIENCE').toUpperCase()}</span>
          <h3 className="text-white font-bold text-sm uppercase truncate">{name || 'Untitled Experience'}</h3>
        </div>
        <span className={`text-[10px] font-bold tracking-widest px-2.5 py-1 shrink-0 ${active ? 'bg-primary/10 text-primary' : 'bg-white/5 text-white/30'}`}>
          {active ? 'ACTIVE' : 'HIDDEN'}
        </span>
      </div>

      {isNew && (
        <div className="mb-4">
          <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">KEY (unique, e.g. drift_experience)</Label>
          <Input value={key} onChange={(e) => setKey(e.target.value)} placeholder="my_new_experience" className={inputCls} />
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">NAME</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
        </div>
        <div>
          <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">TAG (e.g. AGES 16+)</Label>
          <Input value={tag} onChange={(e) => setTag(e.target.value)} className={inputCls} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">PRICE (£)</Label>
          <Input type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className={inputCls} />
        </div>
        <div>
          <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">DRIVE TIME (MINUTES)</Label>
          <Input type="number" min="1" max="50" step="1" value={durationMinutes} onChange={(e) => setDurationMinutes(e.target.value)} className={inputCls} />
          <p className="text-white/20 text-[10px] mt-1.5">Max 50 — a 10-min changeover is added between slots.</p>
        </div>
      </div>

      <div className="mb-4">
        <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">FEATURES (one per line)</Label>
        <Textarea value={specs} onChange={(e) => setSpecs(e.target.value)} rows={5} className="bg-[#111115] border-white/10 text-white placeholder:text-white/20 focus:border-primary/60" />
      </div>

      {/* Rig Selection — replaces the old requires_rig toggle */}
      <div className="mb-5">
        <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">ELIGIBLE RIGS</Label>
        <p className="text-white/20 text-[10px] mb-3">Select which rigs this experience can use. Inappropriate rigs (e.g. adult rigs for kids) won't show on the booking or walk-in rig picker. At least one must be selected.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {rigList.map(({ key: rigKey, ...rig }) => {
            const checked = allowedRigs.includes(rigKey);
            return (
              <button
                key={rigKey}
                type="button"
                onClick={() => toggleRig(rigKey)}
                className={`flex items-center gap-3 px-3 py-2.5 border text-left transition-colors ${
                  checked ? 'border-primary/40 bg-primary/10' : 'border-white/10 bg-[#0d0d0f] hover:border-white/20'
                }`}
              >
                <div className={`w-5 h-5 border-2 flex items-center justify-center shrink-0 transition-all ${
                  checked ? 'border-primary bg-primary' : 'border-white/20'
                }`}>
                  {checked && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                </div>
                <div className="min-w-0">
                  <span className={`text-[9px] font-bold tracking-widest block ${checked ? 'text-primary' : 'text-white/30'}`}>{rig.tag}</span>
                  <span className={`text-xs font-bold uppercase truncate block ${checked ? 'text-white' : 'text-white/50'}`}>{rig.name}</span>
                </div>
              </button>
            );
          })}
        </div>
        {allowedRigs.length === 0 && (
          <p className="text-red-400 text-[10px] mt-2 font-bold tracking-wider">Select at least one rig to save.</p>
        )}
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-white/5 gap-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setActive((a) => !a)}
            className={`text-xs font-bold tracking-wider px-3 py-2 border transition-colors ${active ? 'border-primary/30 text-primary hover:bg-primary/5' : 'border-white/10 text-white/40 hover:text-white'}`}
          >
            {active ? 'ACTIVE' : 'HIDDEN'}
          </button>
          {canDelete && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              className="flex items-center gap-1.5 text-xs font-bold tracking-wider px-3 py-2 border border-red-500/20 text-red-400 hover:bg-red-500/5 transition-colors disabled:opacity-40"
            >
              {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} DELETE
            </button>
          )}
        </div>
        <button
          onClick={handleSave}
          disabled={!canSave}
          className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-wider px-5 py-2.5 transition-all disabled:opacity-40"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
          {saving ? 'SAVING' : saved ? 'SAVED' : 'SAVE'}
        </button>
      </div>
    </div>
  );
}